import React, { useState, useEffect, useCallback } from 'react';
import './ProxyNodeTest.css';

const API_BASE = '/api';

function ProxyNodeTest() {
  const [proxies, setProxies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProxy, setSelectedProxy] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [results, setResults] = useState([]); // test history in-memory

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchProxies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/proxy-node-test/proxies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setProxies(data.data || []);
    } catch (err) {
      console.error('Proxy listesi alınamadı:', err);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchProxies(); }, [fetchProxies]);

  const handleTest = async (proxyId) => {
    setTestLoading(true);
    const startTime = Date.now();
    try {
      const res = await fetch(`${API_BASE}/proxy-node-test/${proxyId}`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      const totalElapsed = Date.now() - startTime;
      setResults(prev => [{
        id: Date.now(),
        proxyId,
        proxyName: data.proxy?.name || 'İsimsiz',
        proxyUrl: data.proxy?.proxyUrl || '',
        success: data.success,
        ip: data.data?.ip || '',
        status: data.data?.status || 0,
        elapsed: data.elapsed || totalElapsed,
        error: data.message || null,
        timestamp: new Date(),
      }, ...prev]);
    } catch (err) {
      setResults(prev => [{
        id: Date.now(),
        proxyId,
        proxyName: '',
        proxyUrl: '',
        success: false,
        error: err.message,
        elapsed: Date.now() - startTime,
        timestamp: new Date(),
      }, ...prev]);
    }
    setTestLoading(false);
  };

  const handleTestAll = async () => {
    for (const proxy of proxies) {
      setSelectedProxy(proxy._id);
      setTestLoading(true);
      const startTime = Date.now();
      try {
        const res = await fetch(`${API_BASE}/proxy-node-test/${proxy._id}`, {
          method: 'POST',
          headers,
        });
        const data = await res.json();
        const totalElapsed = Date.now() - startTime;
        setResults(prev => [{
          id: Date.now(),
          proxyId: proxy._id,
          proxyName: data.proxy?.name || proxy.name || 'İsimsiz',
          proxyUrl: data.proxy?.proxyUrl || proxy.proxyUrl || '',
          success: data.success,
          ip: data.data?.ip || '',
          status: data.data?.status || 0,
          elapsed: data.elapsed || totalElapsed,
          error: data.message || null,
          timestamp: new Date(),
        }, ...prev]);
      } catch (err) {
        setResults(prev => [{
          id: Date.now(),
          proxyId: proxy._id,
          proxyName: proxy.name || 'İsimsiz',
          proxyUrl: proxy.proxyUrl || '',
          success: false,
          error: err.message,
          elapsed: Date.now() - startTime,
          timestamp: new Date(),
        }, ...prev]);
      }
    }
    setTestLoading(false);
    setSelectedProxy(null);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' }) : '-';

  return (
    <div className="pnt-page">
      <div className="pnt-header">
        <h2>🔌 Node Proxy Test</h2>
        <span className="pnt-subtitle">HttpsProxyAgent ile test (Play/Validate ile aynı yöntem)</span>
      </div>

      {/* Proxy List + Test Buttons */}
      <div className="pnt-card">
        <div className="pnt-card-header">
          <h3>📋 Proxy Listesi</h3>
          <button
            className="pnt-test-all-btn"
            onClick={handleTestAll}
            disabled={testLoading || proxies.length === 0}
          >
            {testLoading ? '⏳ Test ediliyor...' : '▶ Tümünü Test Et'}
          </button>
        </div>

        {loading ? (
          <div className="pnt-loading">Yükleniyor...</div>
        ) : proxies.length === 0 ? (
          <p className="pnt-empty">Henüz proxy eklenmemiş.</p>
        ) : (
          <div className="pnt-proxy-list">
            {proxies.map(proxy => (
              <div key={proxy._id} className={`pnt-proxy-item ${selectedProxy === proxy._id && testLoading ? 'pnt-testing' : ''}`}>
                <div className="pnt-proxy-info">
                  <span className="pnt-proxy-name">{proxy.name || 'İsimsiz'}</span>
                  <span className="pnt-proxy-url">{proxy.proxyUrl || '-'}</span>
                </div>
                <button
                  className="pnt-test-btn"
                  onClick={() => { setSelectedProxy(proxy._id); handleTest(proxy._id); }}
                  disabled={testLoading}
                >
                  {testLoading && selectedProxy === proxy._id ? '⏳' : '▶ Test'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className="pnt-card">
          <div className="pnt-card-header">
            <h3>📊 Test Sonuçları ({results.length})</h3>
            <button className="pnt-clear-btn" onClick={() => setResults([])}>Temizle</button>
          </div>
          <div className="pnt-results">
            {results.map(r => (
              <div key={r.id} className={`pnt-result-item ${r.success ? 'pnt-ok' : 'pnt-fail'}`}>
                <span className={`pnt-badge ${r.success ? 'pnt-badge-ok' : 'pnt-badge-fail'}`}>
                  {r.success ? '✅' : '❌'}
                </span>
                <span className="pnt-result-name">{r.proxyName}</span>
                <span className="pnt-result-url">{r.proxyUrl}</span>
                {r.success ? (
                  <>
                    <span className="pnt-result-ip">{r.ip}</span>
                    <span className="pnt-result-status">HTTP {r.status}</span>
                  </>
                ) : (
                  <span className="pnt-result-error">{r.error}</span>
                )}
                <span className="pnt-result-elapsed">{r.elapsed}ms</span>
                <span className="pnt-result-time">{formatDate(r.timestamp)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProxyNodeTest;
